import { assign, isEmpty, omitBy } from "lodash";
import { ChangeDetectionStrategy, Component, ElementRef, signal, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import type { MenuItem } from "primeng/api";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import * as FlatServices from '@medsurf/flat-services';
import { ZodSharedValidator } from '@medsurf/flat-validators';
import dayjs from "dayjs";

@Component({
    selector: 'medsurf-slide-edit-metadata',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './metadata.component.html',
    styleUrls: ['./metadata.component.scss'],
    standalone: false
})
export class MetadataComponent {
  @ViewChild('edit_overlay_template_scroll_panel', {static: false}) editPanelScrollPanel: ElementRef<HTMLDivElement>;
  
  isGenerating = signal(false);
  #isSubmitted = signal(false);
  #image: FlatModels.ImageEntityModels.Image | null;
  inheritedSettings: { [entity: string]: boolean | string | null } | null;
  page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training | null;
  node: FlatModels.NodeEntityModels.Node | null;
  pageForm = new FormGroup(
    {
      title: new FormControl(''),
      description: new FormControl(''),
      modality: new FormControl(''),
      status: new FormControl(''),
      authorNote: new FormControl(''),
      sideBarSize: new FormControl(3),
      thumbnailMedia: new FormControl(''),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.PageEntityModels.SlideSchema.pick({
          title: true,
          description: true,
          modality: true,
          status: true,
          authorNote: true,
          sideBarSize: true,
          thumbnailMedia: true,
          defaults: true
        })
      ),
      updateOn: 'change',
    }
  );
  nodeForm = new FormGroup(
    {
      hidden: new FormControl(false)
    },
    {
      validators: ZodSharedValidator.validate(
          FlatModels.NodeEntityModels.NodeSchema.pick({
            hidden: true,
          })
        ),
        updateOn: 'change',
    }
  );
  layoutForm = new FormGroup(
    {
      color: new FormControl<string | null>(null),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.ImageEntityModels.BackgroundSchema.pick({
          color: true
        })
      ),
      updateOn: 'change',
    }
  );
  preferencesForm = new FormGroup(
    {
      labelColor: new FormControl(''),
      pointerColor: new FormControl(''),
      labelFontSize: new FormControl(0)
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.PageEntityModels.DefaultsSchema.pick({
          labelColor: true,
          pointerColor: true,
          labelFontSize: true,
        })
      ),
      updateOn: 'change',
    }
  );
  nodeSettingsForm = new FormGroup(
    {
      hideAnnotations: new FormControl(false),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.NodeEntityModels.NodeSettingsSchema.pick({
          hideAnnotations: true
        }),
      ),
      updateOn: 'change',
    }
  );
	pdfMenuItems: MenuItem[] = [
    {
      label: this.translate.instant('global.pdf_for_viewer'),
      icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PDF,
      command: () => this.#generatePdf()
    },
    {
      label: this.translate.instant('global.pdf_for_author'),
      icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PDF,
      command: () => this.#generateAuthorPdf()
    },
  ];
  readonly FlatModels = FlatModels;
  
  constructor(private alertControlFacade: FlatFacades.AlertControlFacade,
              public clipboardAuthorFacade: FlatFacades.ClipboardAuthorFacade,
              public historySharedFacade: FlatFacades.HistorySharedFacade,
              public nodeSharedFacade: FlatFacades.NodeSharedFacade,
              public slideAuthorFacade: FlatFacades.SlideAuthorFacade,
              public mediaControlService: FlatServices.MediaControlService,
              private translate: TranslateService) {
    // Update Node Settings
    this.nodeSharedFacade.currentSelectedNodeSettings$.pipe(takeUntilDestroyed()).subscribe((currentSelectedNodeSettings) => {
      if (currentSelectedNodeSettings) {
        if (this.node?.id !== currentSelectedNodeSettings.node.id && this.editPanelScrollPanel) {
          this.editPanelScrollPanel.nativeElement.scrollTop = 0;
        }
        this.node = structuredClone(currentSelectedNodeSettings.node);
        this.nodeForm.reset(this.node);
        this.inheritedSettings = currentSelectedNodeSettings.settings.inheritedWithDefaults;
        this.nodeSettingsForm.reset(currentSelectedNodeSettings.settings.combinedWithDefaults);
      } else {
        this.node = null;
        this.inheritedSettings = null;
        this.nodeSettingsForm.reset(FlatModels.NodeEntityModels.nodeSettingDefaults);
      }
      this.#isSubmitted.set(false);
    });
    // Update Page
    this.slideAuthorFacade.currentSelectedSlidePage$.pipe(takeUntilDestroyed()).subscribe((page) => {
      this.page = structuredClone(page);
      if (this.page) {
        this.pageForm.reset(this.page);
        if (this.page.defaults) {
          this.preferencesForm.reset({
            labelColor: this.page.defaults.labelColor,
            pointerColor: this.page.defaults.pointerColor,
            labelFontSize: this.page.defaults.labelFontSize,
          });
        }
      }
      this.#isSubmitted.set(false);
    });
    // Update Image
    this.slideAuthorFacade.currentSelectedSlideImage$.pipe(takeUntilDestroyed()).subscribe((image) => {
      this.#image = structuredClone(image);
      if (this.#image && this.#image.background) {
        this.layoutForm.reset({
          color: this.#image.background.color
        });
      }
      this.#isSubmitted.set(false);
    });
  }
  
  onMediaChange(mediaId: string | null) {
    this.pageForm.patchValue({thumbnailMedia: mediaId});
    return this.onSubmit();
  }
  
  onSubmit() {
    if (this.#isSubmitted() || this.pageForm.invalid) {
      return;
    }
    this.#isSubmitted.set(true);
    
    // Update Page
    const page = assign(structuredClone(this.page), this.pageForm.value);
    // Update Defaults
    page.defaults.labelColor = this.preferencesForm.value['labelColor'] || null;
    page.defaults.pointerColor = this.preferencesForm.value['pointerColor'] || null;
    page.defaults.labelFontSize = this.preferencesForm.value['labelFontSize'] || null;
    // Update Route
    this.nodeSharedFacade.requestUpdatePage(page, 'history.page_update_settings').subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.pageForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      }
    });
  }

  onNodeSubmit() {
    if (this.#isSubmitted() || this.nodeForm.invalid) {
      return;
    }
    this.#isSubmitted.set(true);

    // Update Node
    const node = assign(structuredClone(this.node), this.nodeForm.value);
    this.nodeSharedFacade.requestUpdateNode(node, 'history.node_update_settings').subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.nodeForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      }
    });
  }
  
  onNodeSettingsSubmit() {
    if (this.#isSubmitted() || this.nodeSettingsForm.invalid || !this.node) {
      return;
    }
    this.#isSubmitted.set(true);
    const newSettings = omitBy({...this.nodeSettingsForm.value}, (v, k) => v === this.inheritedSettings?.[k]);
    
    // Update Node Settings
    this.nodeSharedFacade.requestUpdateNodeSettings(this.node.id, isEmpty(newSettings) ? null : newSettings).subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.nodeSettingsForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      },
    });
  }
  
  onLayoutSubmit() {
    if (this.#isSubmitted() || this.layoutForm.invalid || !this.#image) {
      return;
    }
    this.#isSubmitted.set(true);
    
    // Update Layout
    const image = structuredClone(this.#image);
    if (!image.background) {
      image.background = { color: null, blendMode: FlatModels.ImageEntityModels.BlendMode.NORMAL };
    }
    image.background.color = this.layoutForm.value.color || null;
    
    this.slideAuthorFacade.requestUpdateImage(image, 'history.image_update').subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.layoutForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      }
    });
  }

  async downloadPdf(media: FlatModels.MediaEntityModels.MediaEntityType, type: 'author'| 'viewer') {
    if (!media) return;
    const url = this.mediaControlService.getPDFDownloadUrl(media, type);
    window.open(url, '_blank');
  } 

  #generatePdf() {
    if (this.isGenerating() || !this.page) {
      return;
    }
    this.isGenerating.set(true);
    this.nodeSharedFacade.requestGenerateSlidePdf(this.page.id, false).subscribe({
      error: (result) => {
        this.alertControlFacade.requestAddError(result, 'notification.slide_pdf_failed');
        this.isGenerating.set(false);
      },
      next: () => {
        this.alertControlFacade.requestAddAlert({
          key: 'main',
          detail: 'notification.slide_pdf_generated',
          severity: FlatModels.AlertControlModels.AlertType.enum.success,
          life: FlatModels.AlertControlModels.AlertTime.DEFAULT
        });
        this.isGenerating.set(false);
      }
    });
  }
  
  #generateAuthorPdf() {
    if (this.isGenerating() || !this.page) {
      return;
    }
    this.isGenerating.set(true);
    this.nodeSharedFacade.requestGenerateSlidePdf(this.page.id, true).subscribe({
      error: (result) => {
        this.alertControlFacade.requestAddError(result, 'notification.slide_pdf_failed');
        this.isGenerating.set(false);
      },
      next: () => {
        this.alertControlFacade.requestAddAlert({
          key: 'main',
          detail: 'notification.slide_pdf_generated',
          severity: FlatModels.AlertControlModels.AlertType.enum.success,
          life: FlatModels.AlertControlModels.AlertTime.DEFAULT
        });
        this.isGenerating.set(false);
      }
    });
  }
}
